import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { AuditingRequest, AuditingStatsResponse, ReportingComparison } from '../../types/reporting';

interface AuditingInput extends AuditingRequest {
    comparison?: ReportingComparison;
    includeComparison?: boolean;
}

const getAuditingStats = async (
    sessionId: string,
    input: AuditingInput
): Promise<AuditingStatsResponse> => {
    const { data } = await axios.post(
        `${API_URL}/reporting/auditing/stats`,
        input,
        { headers: { Authorization: sessionId } }
    );
    return data;
};

export const useAuditingStats = (
    startDate: string,
    endDate: string,
    options?: {
        locationId?: string;
        insuranceId?: string;
        oemId?: string;
        viewMode?: 'orders' | 'parts';
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        minDaysActive?: number;
        maxDaysActive?: number;
        preset?: string;
        comparison?: ReportingComparison;
        excludeEvs?: boolean;
    }
) => {
    const sessionContext = useContext(SessionContext);
    const { comparison, excludeEvs, ...restOptions } = options || {};

    return useQuery({
        queryKey: ['reporting', 'auditing', startDate, endDate, options],
        queryFn: () => getAuditingStats(sessionContext?.sessionId ?? '', {
            startDate,
            endDate,
            ...restOptions,
            comparison,
            includeComparison: !!comparison,
            excludeEvs,
        }),
        enabled: !!sessionContext?.sessionId && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 5,
    });
};
